import { z } from 'zod';

import { FEATURE_NOT_ENABLED, PLAN_INACTIVE } from '@crv/contracts/billing';
import {
  CASH_EXPENSE_ALREADY_VOIDED,
  CASH_DRAWER_REFILL_SELF_LIMIT_EXCEEDED,
  CASH_DRAWER_INSUFFICIENT_FUNDS,
  CASH_EXPENSE_NOT_FOUND,
  CASH_EXPENSE_CATEGORY_ALREADY_DISABLED,
  CASH_EXPENSE_CATEGORY_INACTIVE,
  CASH_EXPENSE_CATEGORY_NOT_FOUND,
  CASH_LOCATION_ALREADY_DISABLED,
  CASH_LOCATION_INACTIVE,
  CASH_LOCATION_INVALID_TYPE,
  CASH_LOCATION_NOT_FOUND,
  CASH_SESSION_ALREADY_OPEN,
  CASH_SESSION_CLOSED,
  CASH_SESSION_DENOMINATION_MISMATCH,
  CASH_SESSION_DENOMINATIONS_REQUIRED,
  CASH_SESSION_NOT_FOUND,
  CASH_SESSION_NOT_OPEN,
  CASH_STATION_CONTEXT_REQUIRED,
  CASH_TRANSFER_INVALID,
  CASH_TRANSFER_NOT_FOUND,
  CASH_TREASURY_ACTION_BLOCKED_OFFLINE_ONLY,
  CASH_TREASURY_ACTION_FORBIDDEN,
  CASH_TREASURY_COUNT_DENOMINATION_REQUIRED,
  CASH_TREASURY_COUNT_DENOMINATION_TOTAL_MISMATCH,
  CASH_TREASURY_COUNT_IDEMPOTENCY_CONFLICT,
  CASH_TREASURY_COUNT_IN_PROGRESS,
  CASH_TREASURY_COUNT_LOCK_NOT_FOUND,
  CASH_TREASURY_COUNT_LOCK_STALE,
  CASH_TREASURY_COUNT_LOCK_VERSION_MISMATCH,
  CASH_TREASURY_COUNT_STALE_EXPECTED_BALANCE,
  CASH_TREASURY_DISTRIBUTION_INSUFFICIENT_AVAILABLE_BALANCE,
  CASH_TREASURY_DISTRIBUTION_MIRROR_NOT_READY,
  CASH_TREASURY_DISTRIBUTION_REQUEST_INVALID_STATE,
  CASH_TREASURY_DISTRIBUTION_REQUEST_NOT_FOUND,
  CASH_TREASURY_DISTRIBUTION_TARGET_INVALID,
  CASH_TREASURY_STARTING_COUNT_ALREADY_EXISTS,
} from '@crv/contracts/cash';
import {
  PURCHASES_COMMIT_AMBIGUOUS_CASH_SESSION,
  PURCHASES_COMMIT_INVALID_CASH_SESSION,
  PURCHASES_COMMIT_REQUIRES_CASH_SESSION,
} from '@crv/contracts/purchases';
import {
  PERMISSION_DENIED,
  ROLE_NAME_ALREADY_EXISTS,
  ROLE_SELF_LOCKOUT,
} from '@crv/contracts/roles';
import { TENANCY_COMPANY_SUSPENDED } from '@crv/contracts/tenancy';
import {
  IMPORT_PACK_ALREADY_REJECTED,
  IMPORT_PACK_COMPANY_MISMATCH,
  IMPORT_PACK_INVALID_ENVELOPE,
  IMPORT_PACK_INVALID_PAYLOAD,
  IMPORT_PACK_UNSUPPORTED_TYPE,
  IMPORT_PACKS_ACCESS_DENIED,
} from '@crv/contracts/import-packs';
import {
  MATERIALS_CATALOG_ACCESS_DENIED,
  MATERIALS_CATALOG_DUPLICATE_DISPLAY_NAME,
  MATERIALS_CATALOG_GLOBAL_READ_ONLY,
  MATERIALS_CATALOG_INVALID_OVERRIDE,
  MATERIALS_CATALOG_INVALID_PRESENTATION,
  MATERIALS_CATALOG_LIMIT_EXCEEDED,
  MATERIALS_CATALOG_MATERIAL_NOT_FOUND,
  MATERIALS_CATALOG_OVERRIDE_NOT_FOUND,
} from '@crv/contracts/materials-catalog';
import {
  MATERIALS_PRESENTATION_CATEGORY_OVERLAP,
  MATERIALS_PRESENTATION_DUPLICATE_CARD_REF,
  MATERIALS_PRESENTATION_DUPLICATE_CATEGORY_NAME,
  MATERIALS_PRESENTATION_INVALID_CARD_REF,
  MATERIALS_PRESENTATION_STATION_OVERRIDE_ONLY,
  MATERIALS_PRESENTATION_VERSION_CONFLICT,
} from '@crv/contracts/materials-presentation';
import {
  REGULATORY_DIRECTORY_CANDIDATE_NOT_FOUND,
  REGULATORY_DIRECTORY_ENTITY_NOT_FOUND,
  REGULATORY_DIRECTORY_IDENTITY_IMMUTABLE,
  REGULATORY_DIRECTORY_INVALID_STATE,
  REGULATORY_DIRECTORY_LINK_CONFLICT,
  REGULATORY_DIRECTORY_MERGE_IDENTITY_MISMATCH,
  REGULATORY_DIRECTORY_PLATFORM_PERMISSION_REQUIRED,
} from '@crv/contracts/regulatory-directory';
import {
  SHIPMENT_DEPARTURE_NO_ELIGIBLE_HOLDING_UNITS,
  SHIPMENT_DEPARTED_REQUIRED_FOR_PROCESSOR_TICKET,
  SHIPMENT_PROCESSOR_TICKET_ALREADY_EXISTS,
  SHIPMENT_SCALE_TICKET_INVALID_WEIGHTS,
  SHIPMENTS_ALREADY_LOCKED,
  SHIPMENTS_ALREADY_SETTLED,
  SHIPMENTS_INVALID_STATE_TRANSITION,
  SHIPMENTS_LINES_ALREADY_SHIPPED,
  SHIPMENTS_MISSING_SW_RATE,
  SHIPMENTS_NOT_FOUND,
  SHIPMENTS_OVERLAP,
  SHIPMENTS_PERMISSION_DENIED,
  SHIPMENTS_RC_SYNC_GATE_FAILED,
  SHIPMENTS_SCALE_TICKET_IMMUTABLE,
  SHIPMENTS_SCALE_TICKET_NOT_FOUND,
  SHIPMENTS_STATION_CONTEXT_REQUIRED,
  SHIPMENTS_WRITE_BLOCKED_OFFLINE_ONLY,
  SHIPMENTS_REPORTED_BOUNDARY_CONFLICT,
  SHIPMENTS_VOID_REQUIRES_REVERSAL,
  SHIPMENTS_INVALID_STATE_FOR_VOID,
  SHIPMENTS_VALIDATION_FAILED,
  SHIPMENTS_VOID_NOT_ALLOWED,
  SHIPMENT_DEPARTURE_REQUIRES_HOLDING_UNITS,
  SHIPMENT_TRACKING_NOT_ENABLED,
} from '@crv/contracts/shipments';
import {
  STATION_OPERATIONS_FINAL_ACTION_BLOCKED,
  STATION_OPERATIONS_JOIN_FAILED,
  STATION_OPERATIONS_JOIN_REQUIRED,
  STATION_OPERATIONS_PRIMARY_REASSIGNMENT_BLOCKED,
  STATION_OPERATIONS_SESSION_NOT_VISIBLE,
} from '@crv/contracts/station-operations';
import type { ReadinessBlockingReason } from '@crv/contracts/station-operations';

import { ApiClientError } from './http-client';

export interface ApiErrorInfo {
  code: string | null;
  message: string;
  details: string | null;
}

export function getApiErrorInfo(error: unknown): ApiErrorInfo {
  if (error instanceof ApiClientError) {
    const message = error.body?.message ?? error.message;
    const detailsParts: string[] = [];
    const code = error.body?.code ?? null;

    if (error.body) {
      detailsParts.push(`Code: ${error.body.code}`);
      if (error.body.traceId && error.body.traceId !== 'unknown') {
        detailsParts.push(`Trace: ${error.body.traceId}`);
      }
      const reason = extractErrorReason(error.body.details);
      if (reason) {
        detailsParts.push(`Reason: ${reason}`);
      }
    }

    return { code, message, details: detailsParts.length > 0 ? detailsParts.join(' | ') : null };
  }

  if (error instanceof Error) {
    return { code: null, message: error.message, details: null };
  }

  return { code: null, message: 'Unexpected error occurred', details: null };
}

export function getApiErrorUserMessage(error: unknown): string {
  const info = getApiErrorInfo(error);
  const readinessBlockingReason = extractReadinessBlockingReasonFromError(error);

  switch (info.code) {
    case FEATURE_NOT_ENABLED:
      return 'This feature is not enabled for your plan. Contact an administrator to upgrade or enable it.';
    case PLAN_INACTIVE:
      return 'This company plan is inactive. Contact an administrator or billing to reactivate it.';
    case TENANCY_COMPANY_SUSPENDED:
      return 'This company is suspended. Choose another company or contact support.';
    case PERMISSION_DENIED:
      return 'You do not have permission to perform this action. Ask a manager to grant access.';
    case IMPORT_PACKS_ACCESS_DENIED:
      return 'You do not have permission to apply import packs for this company.';
    case IMPORT_PACK_ALREADY_REJECTED:
      return 'This import pack was previously rejected. Request a new pack and try again.';
    case IMPORT_PACK_INVALID_ENVELOPE:
      return 'This file is not a valid import pack envelope.';
    case IMPORT_PACK_INVALID_PAYLOAD:
      return 'The import pack payload is invalid. Request a new pack.';
    case IMPORT_PACK_UNSUPPORTED_TYPE:
      return 'This import pack type is not supported yet.';
    case IMPORT_PACK_COMPANY_MISMATCH:
      return 'The import pack company does not match the selected company.';
    case CASH_LOCATION_NOT_FOUND:
      return 'Cash location not found.';
    case CASH_LOCATION_ALREADY_DISABLED:
      return 'Cash location is already disabled.';
    case CASH_LOCATION_INACTIVE:
      return 'Cash location is inactive.';
    case CASH_LOCATION_INVALID_TYPE:
      return 'Cash location type is not valid for this action.';
    case CASH_STATION_CONTEXT_REQUIRED:
      return 'Station context is required for this cash action.';
    case CASH_SESSION_NOT_FOUND:
      return 'Cash session not found.';
    case CASH_SESSION_ALREADY_OPEN:
      return 'A cash session is already open for this drawer.';
    case CASH_SESSION_NOT_OPEN:
      return 'Cash session must be open to complete this action.';
    case CASH_SESSION_CLOSED:
      return 'Cash session is already closed.';
    case CASH_SESSION_DENOMINATIONS_REQUIRED:
      return 'Enter cash breakdown counts to close this drawer.';
    case CASH_SESSION_DENOMINATION_MISMATCH:
      return 'Cash breakdown total does not match the counted cash. Recount or update the counts so totals match.';
    case CASH_EXPENSE_NOT_FOUND:
      return 'Cash expense not found.';
    case CASH_EXPENSE_ALREADY_VOIDED:
      return 'Cash expense is already voided.';
    case CASH_DRAWER_INSUFFICIENT_FUNDS:
      return 'Not enough cash in the drawer for this payout. Refill or transfer cash through Cash and try again.';
    case CASH_DRAWER_REFILL_SELF_LIMIT_EXCEEDED:
      return 'This refill is above the cashier self-refill limit. Ask a Lead Cashier or Manager.';
    case PURCHASES_COMMIT_REQUIRES_CASH_SESSION:
      return 'Open a cash session before committing a purchase.';
    case PURCHASES_COMMIT_AMBIGUOUS_CASH_SESSION:
      return 'More than one drawer session is open. Select the drawer for this payout.';
    case PURCHASES_COMMIT_INVALID_CASH_SESSION:
      return 'Selected cash session is not valid for this payout. Refresh Cash or choose another drawer.';
    case CASH_EXPENSE_CATEGORY_NOT_FOUND:
      return 'Expense category not found.';
    case CASH_EXPENSE_CATEGORY_INACTIVE:
      return 'Expense category is inactive.';
    case CASH_EXPENSE_CATEGORY_ALREADY_DISABLED:
      return 'Expense category is already disabled.';
    case CASH_TRANSFER_NOT_FOUND:
      return 'Cash transfer not found.';
    case CASH_TRANSFER_INVALID:
      return 'Cash transfer is not valid for the selected locations.';
    case CASH_TREASURY_ACTION_FORBIDDEN:
      return 'Treasury actions are not allowed with station context.';
    case CASH_TREASURY_ACTION_BLOCKED_OFFLINE_ONLY:
      return 'Treasury actions are blocked in offline-only mode.';
    case CASH_TREASURY_COUNT_IN_PROGRESS:
      return 'Another Treasury count is already in progress. Wait for it to expire or ask an authorized user to cancel it.';
    case CASH_TREASURY_COUNT_LOCK_NOT_FOUND:
      return 'Treasury count draft not found. Start a new count.';
    case CASH_TREASURY_COUNT_LOCK_STALE:
      return 'This Treasury count draft is no longer active. Start a new count.';
    case CASH_TREASURY_COUNT_LOCK_VERSION_MISMATCH:
      return 'This Treasury count changed after it was opened. Start a new count.';
    case CASH_TREASURY_COUNT_STALE_EXPECTED_BALANCE:
      return 'Expected balance changed after this count started. Start a new count.';
    case CASH_TREASURY_COUNT_DENOMINATION_REQUIRED:
      return 'Enter cash breakdown rows for this Treasury count.';
    case CASH_TREASURY_COUNT_DENOMINATION_TOTAL_MISMATCH:
      return 'Cash breakdown total must match the counted amount.';
    case CASH_TREASURY_COUNT_IDEMPOTENCY_CONFLICT:
      return 'This count submission was already used with different values. Start a new count.';
    case CASH_TREASURY_STARTING_COUNT_ALREADY_EXISTS:
      return 'Starting count is only available before any Treasury count evidence exists.';
    case CASH_TREASURY_DISTRIBUTION_REQUEST_NOT_FOUND:
      return 'Treasury distribution request not found.';
    case CASH_TREASURY_DISTRIBUTION_REQUEST_INVALID_STATE:
      return 'This Treasury distribution request cannot be changed in its current state. Refresh and review the request.';
    case CASH_TREASURY_DISTRIBUTION_INSUFFICIENT_AVAILABLE_BALANCE:
      return 'Not enough available Treasury balance for this request. Refresh availability or enter a smaller amount.';
    case CASH_TREASURY_DISTRIBUTION_MIRROR_NOT_READY:
      return 'Treasury funding has not finished syncing to the station. Sync cash and try the delivery again.';
    case CASH_TREASURY_DISTRIBUTION_TARGET_INVALID:
      return 'Treasury distribution target is not valid. Choose an active vault and station for the same location.';
    case MATERIALS_CATALOG_ACCESS_DENIED:
      return 'You do not have access to the materials catalog for this company.';
    case MATERIALS_CATALOG_GLOBAL_READ_ONLY:
      return 'Global catalog entries are read-only for tenant users.';
    case MATERIALS_CATALOG_MATERIAL_NOT_FOUND:
      return 'Material not found.';
    case MATERIALS_CATALOG_OVERRIDE_NOT_FOUND:
      return 'Override not found.';
    case MATERIALS_CATALOG_DUPLICATE_DISPLAY_NAME:
      return 'Another material already uses this display name. Choose a unique name.';
    case MATERIALS_CATALOG_INVALID_OVERRIDE:
      return 'The override data is invalid. Review the highlighted fields and try again.';
    case MATERIALS_CATALOG_INVALID_PRESENTATION:
      return 'The material presentation data is invalid.';
    case MATERIALS_CATALOG_LIMIT_EXCEEDED:
      return 'The CRV limit has been exceeded for this material.';
    case MATERIALS_PRESENTATION_VERSION_CONFLICT:
      return 'These presentation settings changed after you loaded the page. Refresh and try again.';
    case MATERIALS_PRESENTATION_INVALID_CARD_REF:
      return 'One or more selected presentation cards are no longer valid. Refresh and review the selections.';
    case MATERIALS_PRESENTATION_DUPLICATE_CARD_REF:
      return 'A presentation card appears more than once. Remove the duplicate and try again.';
    case MATERIALS_PRESENTATION_DUPLICATE_CATEGORY_NAME:
      return 'Category names must be unique.';
    case MATERIALS_PRESENTATION_CATEGORY_OVERLAP:
      return 'A presentation card is assigned to more than one active category. Review category rules and manual members.';
    case MATERIALS_PRESENTATION_STATION_OVERRIDE_ONLY:
      return 'Station presentation overrides are managed in Desktop. Web can only edit company defaults.';
    case REGULATORY_DIRECTORY_PLATFORM_PERMISSION_REQUIRED:
      return 'Platform regulatory directory access is required for this action.';
    case REGULATORY_DIRECTORY_ENTITY_NOT_FOUND:
      return 'The regulatory directory entity was not found.';
    case REGULATORY_DIRECTORY_CANDIDATE_NOT_FOUND:
      return 'The regulatory directory candidate was not found.';
    case REGULATORY_DIRECTORY_INVALID_STATE:
      return 'This regulatory directory action is not allowed in the current state.';
    case REGULATORY_DIRECTORY_MERGE_IDENTITY_MISMATCH:
      return 'Only entities with the same normalized RC/PR identity can be merged.';
    case REGULATORY_DIRECTORY_LINK_CONFLICT:
      return 'This entity cannot be changed until active internal links are resolved.';
    case REGULATORY_DIRECTORY_IDENTITY_IMMUTABLE:
      return 'Canonical RC/PR identity is immutable. Create a new entity instead.';
    case SHIPMENTS_PERMISSION_DENIED:
      return 'You do not have permission to manage shipments.';
    case SHIPMENTS_WRITE_BLOCKED_OFFLINE_ONLY:
      return 'Web write actions are blocked in offline-only mode. Use Desktop station operations.';
    case SHIPMENTS_STATION_CONTEXT_REQUIRED:
      return 'Station context is required for this shipment action.';
    case SHIPMENTS_NOT_FOUND:
      return 'Shipment not found.';
    case SHIPMENTS_INVALID_STATE_TRANSITION:
      return 'This shipment cannot transition to the requested state.';
    case SHIPMENTS_ALREADY_LOCKED:
      return 'Shipment is already audit locked.';
    case SHIPMENTS_ALREADY_SETTLED:
      return 'Shipment is already settled.';
    case SHIPMENTS_VOID_NOT_ALLOWED:
    case SHIPMENTS_INVALID_STATE_FOR_VOID:
      return 'Shipment cannot be voided in its current state.';
    case SHIPMENTS_VOID_REQUIRES_REVERSAL:
      return info.message;
    case SHIPMENTS_OVERLAP:
      return 'Shipment range overlaps with reserved or departed purchases.';
    case SHIPMENTS_LINES_ALREADY_SHIPPED:
      return 'Some shipment lines are already reserved or departed.';
    case SHIPMENTS_MISSING_SW_RATE:
      return 'Missing SW rate for one or more shipment lines.';
    case SHIPMENTS_VALIDATION_FAILED:
      return 'Shipment validation failed. Review the inputs and try again.';
    case SHIPMENTS_REPORTED_BOUNDARY_CONFLICT:
      return 'This range overlaps previously reported shipment state. Adjust the start date to the latest reported end or later.';
    case SHIPMENT_DEPARTURE_REQUIRES_HOLDING_UNITS:
      return 'Holding Unit evidence is required before this shipment can be marked departed.';
    case SHIPMENT_DEPARTURE_NO_ELIGIBLE_HOLDING_UNITS:
      return 'No eligible Holding Units were found for the attached evidence. Verify the load session group and unit statuses.';
    case SHIPMENT_TRACKING_NOT_ENABLED:
      return 'Tracking is not enabled for this material/location. Enable tracking before shipping load session evidence.';
    case SHIPMENTS_SCALE_TICKET_NOT_FOUND:
      return 'Scale ticket not found.';
    case SHIPMENTS_SCALE_TICKET_IMMUTABLE:
      return 'Scale ticket cannot be modified in the current shipment state.';
    case SHIPMENT_PROCESSOR_TICKET_ALREADY_EXISTS:
      return 'A PROCESSOR ticket already exists for this shipment.';
    case SHIPMENT_DEPARTED_REQUIRED_FOR_PROCESSOR_TICKET:
      return 'Mark this shipment departed before adding a PROCESSOR ticket.';
    case SHIPMENT_SCALE_TICKET_INVALID_WEIGHTS:
      return 'Gross/tare values are invalid. Gross must be greater than or equal to tare.';
    case SHIPMENTS_RC_SYNC_GATE_FAILED:
      return 'Hybrid sync gate failed while preparing departure. Verify sync worker/configuration and try again.';
    case STATION_OPERATIONS_JOIN_REQUIRED:
      return 'This secondary station must join the current store operating session before operation can begin.';
    case STATION_OPERATIONS_JOIN_FAILED:
      return 'This secondary station could not join the current store session. Reconnect and try again.';
    case STATION_OPERATIONS_SESSION_NOT_VISIBLE:
      return 'The primary station is operating locally and the current store session is not yet visible in cloud.';
    case STATION_OPERATIONS_FINAL_ACTION_BLOCKED:
      if (readinessBlockingReason && readinessBlockingReason !== 'none') {
        return getStationOperationsFinalActionBlockedMessage(readinessBlockingReason);
      }
      return 'Final consolidated shipment actions stay blocked until the store readiness status is green.';
    case STATION_OPERATIONS_PRIMARY_REASSIGNMENT_BLOCKED:
      return 'Primary station assignment cannot change while a store operating session is active.';
    default:
      return info.message;
  }
}

function extractErrorReason(details: unknown): string | null {
  if (!details || typeof details !== 'object') {
    return null;
  }

  const reason = (details as { reason?: unknown }).reason;
  return typeof reason === 'string' && reason.trim().length > 0 ? reason : null;
}

function extractReadinessBlockingReasonFromError(error: unknown): ReadinessBlockingReason | null {
  if (!(error instanceof ApiClientError)) {
    return null;
  }

  return extractReadinessBlockingReason(error.body?.details);
}

function extractReadinessBlockingReason(details: unknown): ReadinessBlockingReason | null {
  if (!details || typeof details !== 'object') {
    return null;
  }

  const blockingReason = (details as { blockingReason?: unknown }).blockingReason;
  return isReadinessBlockingReason(blockingReason) ? blockingReason : null;
}

function isReadinessBlockingReason(value: unknown): value is ReadinessBlockingReason {
  return (
    value === 'none' ||
    value === 'cloud_visibility_delay' ||
    value === 'cloud_unavailable' ||
    value === 'cloud_auth_required' ||
    value === 'join_required' ||
    value === 'joined_station_pending_sync' ||
    value === 'positive_confirmation_missing'
  );
}

function getStationOperationsFinalActionBlockedMessage(
  blockingReason: ReadinessBlockingReason,
): string {
  switch (blockingReason) {
    case 'join_required':
      return 'This station must join the current store session before final shipment actions.';
    case 'cloud_visibility_delay':
      return 'Waiting for the current store session to appear in cloud.';
    case 'cloud_auth_required':
      return 'Cloud sign-in is needed before final shipment actions can be confirmed.';
    case 'cloud_unavailable':
      return 'Cloud access is unavailable right now. Local work is still saved on this device.';
    case 'joined_station_pending_sync':
      return 'Another participating station still has pending updates.';
    case 'positive_confirmation_missing':
      return 'Waiting for store readiness confirmation before final shipment actions.';
    default:
      return 'Final consolidated shipment actions stay blocked until the store readiness status is green.';
  }
}

const validationErrorDetailsSchema = z.object({
  formErrors: z.array(z.string()).optional(),
  fieldErrors: z.record(z.string(), z.array(z.string())).optional(),
});

export type ApiFieldErrors = Record<string, string>;

export function getApiFieldErrors(error: unknown): ApiFieldErrors | null {
  if (!(error instanceof ApiClientError)) {
    return null;
  }

  const apiError = error.body;
  if (
    !apiError ||
    !(
      apiError.code === 'VALIDATION_ERROR' ||
      apiError.code === ROLE_NAME_ALREADY_EXISTS ||
      apiError.code === ROLE_SELF_LOCKOUT ||
      apiError.code === MATERIALS_CATALOG_INVALID_OVERRIDE ||
      apiError.code === MATERIALS_CATALOG_INVALID_PRESENTATION ||
      apiError.code === MATERIALS_PRESENTATION_INVALID_CARD_REF ||
      apiError.code === MATERIALS_PRESENTATION_DUPLICATE_CARD_REF ||
      apiError.code === MATERIALS_PRESENTATION_DUPLICATE_CATEGORY_NAME ||
      apiError.code === MATERIALS_PRESENTATION_CATEGORY_OVERLAP
    )
  ) {
    return null;
  }

  const parsed = validationErrorDetailsSchema.safeParse(apiError.details);
  if (!parsed.success) {
    return null;
  }

  const source = parsed.data.fieldErrors ?? {};
  const fieldErrors: ApiFieldErrors = {};

  for (const [field, messages] of Object.entries(source)) {
    const firstMessage = messages[0];
    if (typeof firstMessage === 'string' && firstMessage.trim().length > 0) {
      fieldErrors[field] = firstMessage;
    }
  }

  return Object.keys(fieldErrors).length > 0 ? fieldErrors : null;
}
